import React from 'react';
import PropTypes from 'prop-types';
import { Table } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { Ref } from '@stardust-ui/react-component-ref';
import { bindActionCreators, compose } from 'redux';
import { injectIntl } from 'react-intl';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import ActivitiesRow from './ActivityRow';
import * as businessCenterActions from '../businessCenter.actions';
import * as constants from '../businessCenter.constants';
import * as activityConstants from '../../Activity/activity.constants';
import * as selectors from '../businessCenter.selectors';

class ActivitiesTable extends React.PureComponent {
  static propTypes = {
    actions: PropTypes.object,
    activities: PropTypes.object.isRequired,
    activityMoveTo: PropTypes.func.isRequired,
    campaignId: PropTypes.string.isRequired,
    campaignOrderIndex: PropTypes.number.isRequired,
    changeActivityStatus: PropTypes.func.isRequired,
    deleteActivity: PropTypes.func.isRequired,
    goToActivity: PropTypes.func.isRequired,
    openActivityInNewTab: PropTypes.func.isRequired,
    highlightedActivityId: PropTypes.string,
    localeDateFormat: PropTypes.string.isRequired,
    localeTimeFormat: PropTypes.string.isRequired,
    businessTimezone: PropTypes.string,
    unpinActivity: PropTypes.func.isRequired,
    pinActivity: PropTypes.func.isRequired,
    openToast: PropTypes.func.isRequired,
    userAccessGroups: PropTypes.object,
    tempActivitiesOrder: PropTypes.object,
    activitiesOrder: PropTypes.object
  };

  constructor(props) {
    super(props);
    const { tempActivitiesOrder, activitiesOrder } = this.props;
    let activitiesTempOrder = [];
    if (tempActivitiesOrder) {
      activitiesTempOrder = tempActivitiesOrder.toJS();
    } else if (activitiesOrder) {
      const fullOrder = activitiesOrder.get(constants.FULL_ORDER);
      activitiesTempOrder = fullOrder ? fullOrder.toJS() : [];
    }
    this.props.actions.setActivitiesTempOrder(activitiesTempOrder, this.props.campaignId);
    this.state = { order: activitiesTempOrder };
  }

  componentDidUpdate(prevProps) {
    // after save the order, the original activities order is changed so 'order' property need to be updated
    if (this.props.tempActivitiesOrder !== undefined && prevProps.tempActivitiesOrder !== this.props.tempActivitiesOrder) {
      this.setState({ order: this.props.tempActivitiesOrder.toJS() });
    } else if (prevProps.activities.size !== this.props.activities.size) {
      // this.props.activities holds only the **rendered** activities (due to filters and sorting)
      // handle case of changing activities list (added new activity, set activities as archive, unarchive, etc.)
      const activities = this.props.activities.toJS();
      const activitiesOrder = this.props.activitiesOrder.toJS();
      const renderedActivitiesIds = activities.map((activity) => activity[activityConstants.HUB_ID]);

      // first order initialization
      if (this.props.tempActivitiesOrder === undefined || this.props.tempActivitiesOrder === null) {
        const order = activitiesOrder[constants.FULL_ORDER].filter((id) => renderedActivitiesIds.includes(id));
        this.props.actions.setActivitiesTempOrder(order, this.props.campaignId);
      } else {
        // update the order state
        const oldOrder = this.state.order || [];
        const activitiesIdsToRemoveFromLocalOrder = oldOrder.filter((id) => !renderedActivitiesIds.includes(id));
        const activitiesIdsToAddToLocalOrder = activities.filter((activity) => !oldOrder.includes(activity[activityConstants.HUB_ID])).map((activity) => activity.hubId);

        let sortedPartialOrder = oldOrder;
        if (activitiesIdsToAddToLocalOrder && activitiesIdsToAddToLocalOrder.length > 0) {
          // in case it's a new campaign (was created)
          const combinedIdsNotOrdered = activitiesIdsToAddToLocalOrder.concat(oldOrder);

          const fullOrderIndexMap = new Map(
            activitiesOrder[constants.FULL_ORDER].map((id, index) => [id, index])
          );

          sortedPartialOrder = combinedIdsNotOrdered.slice().sort(
            (a, b) => fullOrderIndexMap.get(a) - fullOrderIndexMap.get(b)
          );
        } else if (activitiesIdsToRemoveFromLocalOrder && activitiesIdsToRemoveFromLocalOrder.length > 0) {
          sortedPartialOrder = this.state.order.filter((id) => !activitiesIdsToRemoveFromLocalOrder.includes(id));
        }

        this.props.actions.setActivitiesTempOrder(sortedPartialOrder, this.props.campaignId);
      }
    }
  }

  getActivityByHubId = (hubId) => this.props.activities.find((activity) => activity.get('hubId') === hubId)

  render() {
    const { campaignOrderIndex, changeActivityStatus, deleteActivity, campaignId,
      activityMoveTo, highlightedActivityId, businessTimezone, openActivityInNewTab } = this.props;
    const { formatMessage } = this.props.intl; // eslint-disable-line react/prop-types
    const order = this.state.order;
    return (
      <Table className="ui single line fixed selectable very basic table">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell className="list-column-name activity-draggable-column"></Table.HeaderCell>
            <Table.HeaderCell className="list-column-name activity-fav-column"></Table.HeaderCell>
            <Table.HeaderCell className="list-column-name activity-type-column">{formatMessage({ id: 'business-center.campaign.type', defaultMessage: 'type' })}</Table.HeaderCell>
            <Table.HeaderCell className="list-column-name activity-name-column">{formatMessage({ id: 'business-center.campaign.activity', defaultMessage: 'activity' })}</Table.HeaderCell>
            <Table.HeaderCell className="list-column-name activity-trigger-column">{formatMessage({ id: 'business-center.campaign.whenMember', defaultMessage: 'When member' })}</Table.HeaderCell>
            <Table.HeaderCell className="list-column-name activity-actions-column">{formatMessage({ id: 'business-center.campaign.actions', defaultMessage: 'actions' })}</Table.HeaderCell>
            <Table.HeaderCell className="list-column-name activity-status-column">{formatMessage({ id: 'business-center.campaign.status', defaultMessage: 'status' })}</Table.HeaderCell>
            <Table.HeaderCell className="list-column-name activity-badge-column" />
            <Table.HeaderCell className="activity-context-menu-column" />
          </Table.Row>
        </Table.Header>
        <Droppable droppableId={`activities-${campaignId}`} type="activities">
          {(provided, snapshot) => (
            <Ref innerRef={provided.innerRef}>
              <Table.Body {...provided.droppableProps}>
                {order && order.map((item, index) => {
                  const activity = this.getActivityByHubId(item);
                  return activity && (
                    // eslint-disable-next-line react/no-array-index-key
                    <Draggable key={`${item}`} draggableId={item} index={index}>
                      {(provided, snapshot) => (
                        <ActivitiesRow
                          localeDateFormat={this.props.localeDateFormat}
                          localeTimeFormat={this.props.localeTimeFormat}
                          activityMoveTo={activityMoveTo}
                          campaignOrderIndex={campaignOrderIndex}
                          campaignId={campaignId}
                          changeActivityStatus={changeActivityStatus}
                          deleteActivity={deleteActivity}
                          goToActivity={this.props.goToActivity}
                          highlightedActivityId={highlightedActivityId}
                          key={index.toString()}
                          activity={activity}
                          rowIndex={index}
                          businessTimezone={businessTimezone}
                          openActivityInNewTab={openActivityInNewTab}
                          unpinActivity={this.props.unpinActivity}
                          pinActivity={this.props.pinActivity}
                          openToast={this.props.openToast}
                          userAccessGroups={this.props.userAccessGroups}
                          dragRef={provided.innerRef}
                          draggableProps={provided.draggableProps}
                          dragHandleProps={provided.dragHandleProps}
                          isDragging={snapshot.isDragging}
                        />
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </Table.Body>
            </Ref>
          )}
        </Droppable>
      </Table>
    );
  }
}


const mapStateToProps = (state, props) => ({
  tempActivitiesOrder: selectors.getTempActivitiesOrder(state, props.campaignId)
});

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...businessCenterActions }, dispatch)
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(
  injectIntl,
  withConnect
)(ActivitiesTable);
